import React, { useEffect, useMemo, useState } from 'react'

import { useFetchers } from 'react-router-dom'

import { ScoreViewOptions } from '@components/myScores/utils/types'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import Product from '@models/Product'
import Score from '@models/Score'
import { isScore } from '@models/util/score.util'
import { formDataToArray } from '@utils/actions'

import ScoreRow from './ScoreRow'

interface Props {
  scores: (Score | Product)[]
  options?: ScoreViewOptions
}
const ScoreListView: React.FC<Props> = ({ scores, options }) => {
  const [items, setItems] = useState(scores)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const deleteFetcher = useFetchers().find((fetcher) => fetcher.formAction === '/home/deleteScores')
  const deleting = useMemo(
    () => formDataToArray(deleteFetcher?.formData?.get('scoreIds')),
    [deleteFetcher?.formData]
  )

  // Hide collection tags if there are no scores current in a collection
  const hideCollections = useMemo(() => {
    return !scores.some((score) => score.collections && score.collections.length > 0)
  }, [scores])

  // Hide social items if there are no scores visible with any social counts
  const hideSocial = useMemo(() => {
    return !scores.some(
      (score) =>
        !!score.views_count || !!score.favorites_count || (isScore(score) && !!score.comments_count)
    )
  }, [scores])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id === over?.id) return

    setItems((prevItems) => {
      const oldIdx = prevItems.findIndex((item) => item.id === active.id)
      const newIdx = prevItems.findIndex((item) => item.id === over?.id)

      if (typeof options?.onSort === 'function') {
        options.onSort(oldIdx, newIdx)
      }
      return arrayMove(prevItems, oldIdx, newIdx)
    })
  }

  // Update scores if we get an external update
  useEffect(() => {
    setItems(scores)
  }, [scores])

  return (
    <div className="flex flex-col">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}>
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}>
          {items.map((score) => (
            <ScoreRow
              key={`score-row-${score.id}`}
              score={score}
              options={{
                ...options,
                hideCollections: hideCollections || options?.hideCollections,
                hideSocial: hideSocial || options?.hideSocial,
              }}
              pendingDelete={deleting.includes(score.external_id)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  )
}

export default ScoreListView
